/**
 * Tenant Asset Alias Contracts
 *
 * Shared Zod schemas + inferred types for the customer-facing tenant-alias
 * endpoints exposed by `TenantAssetAliasHandlers` (backend):
 *
 *   GET    /api/assets/aliases
 *   DELETE /api/assets/aliases/:aliasId
 *
 * WHY THESE ENDPOINTS EXIST
 * -------------------------
 * Resolving an unknown ticker from the import "Needs attention" tray writes a
 * row into `bat.tenant_asset_aliases` (tenant_id, user_id, source, ticker →
 * asset_id). That row is then the HIGHEST-priority rule in
 * `AssetResolutionService.resolve()` and in the ledger writer's ticker
 * resolution, so it silently classifies every FUTURE import of that ticker on
 * that platform. Until these endpoints existed there was no screen — at any
 * privilege level — that could list or remove one: a mis-click was permanent
 * and invisible. (The super-admin "asset aliases" screens operate on entirely
 * different stores: `exchange_api_templates.asset_aliases` JSONB and the global
 * `exchange_attribute_mappings` table.)
 *
 * Lives in `shared/` because both sides consume the same shape: the backend
 * routes bind these schemas to Fastify request/response validation, and the
 * frontend service uses the inferred types so the panel cannot drift from what
 * the API returns.
 */

import { z } from 'zod/v4'

// ============================================================
// RESPONSE SCHEMAS
// ============================================================

/**
 * One row of `bat.tenant_asset_aliases`, joined to `bat.assets` so the UI can
 * name the asset the ticker maps to instead of printing a bare UUID.
 *
 * `source` is the PLATFORM SLUG the alias is scoped to (e.g. `binance`) — the
 * same ticker can legitimately mean different assets on different platforms,
 * which is why the unique key includes it.
 *
 * `createdAt` is nullable: the column is `defaultNow()` but not `NOT NULL`, so
 * a row written before the default existed can carry null. Serialized as an
 * ISO-8601 string by the handler (never a raw `Date`).
 */
export const TenantAssetAliasRowSchema = z.object({
  id: z.string().uuid(),
  /** Raw ticker as it appears in the platform's export, stored UPPERCASE. */
  ticker: z.string(),
  /**
   * The RAW stored scope value. Not display-safe: it is a catalogue slug
   * ('kraken') for most rows, but the id of a user-defined importer
   * (`bat.import_mappings.id`) or a `bat.exchanges.uuid` for others.
   */
  source: z.string(),
  /**
   * What the UI must PRINT for `source`.
   *  - a slug → the slug itself (unchanged),
   *  - a UUID → the resolved human name ("Mi Kraken Ledger 2021", "Binance"),
   *  - `null` → the id resolved to nothing; the client shows an honest
   *    "unknown platform" label and NEVER falls back to `source`, which would
   *    put a bare UUID in front of the customer again.
   */
  sourceLabel: z.string().nullable(),
  assetId: z.string().uuid(),
  /** Canonical symbol of the target asset, e.g. `POL`. */
  assetSymbol: z.string(),
  /** Human name of the target asset, e.g. `Polygon Ecosystem Token`. */
  assetName: z.string(),
  createdAt: z.string().datetime().nullable(),
})
export type TenantAssetAliasRow = z.infer<typeof TenantAssetAliasRowSchema>

/** `data` payload of `GET /api/assets/aliases`. */
export const ListTenantAssetAliasesResponseSchema = z.object({
  aliases: z.array(TenantAssetAliasRowSchema),
})
export type ListTenantAssetAliasesResponse = z.infer<typeof ListTenantAssetAliasesResponseSchema>

/** `data` payload of `DELETE /api/assets/aliases/:aliasId`. */
export const DeleteTenantAssetAliasResponseSchema = z.object({
  /** Echo of the alias id that was removed — lets the caller confirm WHICH. */
  id: z.string().uuid(),
})
export type DeleteTenantAssetAliasResponse = z.infer<typeof DeleteTenantAssetAliasResponseSchema>

// ============================================================
// REQUEST SCHEMAS
// ============================================================

/** URL params for `DELETE /api/assets/aliases/:aliasId`. */
export const TenantAssetAliasIdParamsSchema = z.object({
  aliasId: z.string().uuid('aliasId must be a UUID'),
})
export type TenantAssetAliasIdParams = z.infer<typeof TenantAssetAliasIdParamsSchema>
